type ConsoleLevel = "log" | "info" | "warn" | "error";

interface InstallOptions {
  endpoint: string;
  enabled: boolean;
}

const consoleLevels: ConsoleLevel[] = ["log", "info", "warn", "error"];

const originalConsole: Record<ConsoleLevel, (...args: unknown[]) => void> = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console)
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function serialiseConsoleArguments(args: unknown[]): { message: string; context: Record<string, unknown> } {
  const [first, ...rest] = args;
  const context: Record<string, unknown> = {};

  if (rest.length > 0) {
    if (rest.length === 1 && isPlainObject(rest[0])) {
      Object.assign(context, rest[0]);
    } else {
      context.arguments = rest;
    }
  }

  if (typeof first === "string") {
    return { message: first, context };
  }

  if (first === undefined) {
    return { message: "", context };
  }

  return { message: JSON.stringify(first), context };
}

async function send(endpoint: string, payload: Record<string, unknown>): Promise<void> {
  try {
    await globalThis.fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });
  } catch (error) {
    // Ignore transport failures in development logging.
    console.debug?.("dev-logging failed", error);
  }
}

export function installDevLogging(options: InstallOptions): void {
  if (!options.enabled) {
    return;
  }

  consoleLevels.forEach((level) => {
    console[level] = (...args: unknown[]) => {
      originalConsole[level](...args);
      const { message, context } = serialiseConsoleArguments(args);
      void send(options.endpoint, {
        source: "console",
        level,
        message,
        context
      });
    };
  });
}
